import { useCallback } from 'react'

import { useNavigate } from 'react-router'

import { isClientError } from '@tribeplatform/gql-client/lib'
import {
  ContentModuleType,
  Event,
  RecurringEventFrequency,
  RecurringEventUpdateMode,
} from '@tribeplatform/gql-client/types'
import { useCreateEvent, useUpdateEvent } from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { RETURN_TO_QUERY_PARAM } from '../../common/constants/content-module.js'
import { useContentModuleUtils, useQueryParam } from '../../common/index.js'
import { FormMethods } from '../../Form/typings.js'
import { getFormUpdatedFields } from '../../Form/utils.js'
import { T, useI18n } from '../../i18n/index.js'
import { Link } from '../../Link/Link.js'
import { isRecurringEvent } from '../EventDetails/utils.js'
import { EventFormFields } from '../types.js'

import { RecurringEventUpdateConfirm } from './RecurringEventUpdateConfirm.js'
import { createCreateEventInput, createUpdateEventInput } from './utils.js'

interface UpdateEventParams {
  formData: EventFormFields
  methods: FormMethods<EventFormFields>
  event: Event
  recurringEventUpdateMode?: RecurringEventUpdateMode
}

interface Props {
  event?: Event
  setEnablePrompt: (enable: boolean) => void
}

export const useSubmitEvent = ({ event, setEnablePrompt }: Props) => {
  const { $t } = useI18n()
  const { mutateAsync: createEvent, isLoading: isCreating } = useCreateEvent()
  const { mutateAsync: updateEvent, isLoading: isUpdating } = useUpdateEvent()
  const navigate = useNavigate()
  const [returnTo] = useQueryParam<string>({
    name: RETURN_TO_QUERY_PARAM,
  })
  const { linkToContentModal } = useContentModuleUtils()

  const onAfterSubmit = useCallback(
    (event: Event, type: 'create' | 'update') => {
      toast({
        custom: ({ onClose }) => (
          <Alert
            status="success"
            withClose
            onClose={onClose}
            title={
              type === 'create'
                ? $t({
                    defaultMessage: 'Event created',
                    id: 'EventForm.EventCreated.Toast.Title',
                  })
                : $t({
                    defaultMessage: 'Event updated',
                    id: 'EventForm.EventUpdated.Toast.Title',
                  })
            }
          >
            <T
              id="EventForm.EventCreated.Toast.Description"
              defaultMessage="<link>Click here</link> to view the event."
              values={{
                link: chunks => (
                  <Link
                    href={linkToContentModal({
                      spaceRelativeUrl: event.space?.relativeUrl ?? '',
                      contentId: event.id,
                      contentType: ContentModuleType.event,
                    })}
                    variant="accent"
                  >
                    {chunks}
                  </Link>
                ),
              }}
            />
          </Alert>
        ),
      })

      /**
       * The returnTo query param is set when the form is opened from admin panel or single event page
       * to redirect the user back to the original page after the event is created or updated.
       */
      if (returnTo) {
        navigate(returnTo)
        return
      }

      navigate(
        linkToContentModal({
          spaceRelativeUrl: event.space?.relativeUrl,
          contentId: event.id,
          contentType: ContentModuleType.event,
        }),
      )
    },
    [$t, linkToContentModal, navigate, returnTo],
  )

  const getUpdateRecurringEventConfirmation = useCallback(
    async (methods: FormMethods<EventFormFields>) => {
      const confirmUpdate =
        methods.formState.dirtyFields.startTime ||
        methods.formState.dirtyFields.endTime ||
        methods.formState.dirtyFields.recurrenceConfig?.frequency ||
        methods.formState.dirtyFields.recurrenceConfig?.recurrenceEndDate ||
        methods.formState.dirtyFields.recurrenceConfig?.repeatsOn

      if (!confirmUpdate) {
        return true
      }

      return confirm({
        title: $t({
          id: 'EventForm.RecurringEvent.Update.Confirmation.Title',
          defaultMessage: 'Update recurring event',
        }),
        description: <RecurringEventUpdateConfirm />,
      })
    },
    [$t],
  )

  const create = useCallback(
    async (formData: EventFormFields) => {
      const createdEvent = await createEvent({
        spaceId: formData.targetSpace?.id,
        input: createCreateEventInput(formData),
      })
      return onAfterSubmit(createdEvent, 'create')
    },
    [createEvent, onAfterSubmit],
  )

  const update = useCallback(
    async ({
      formData,
      methods,
      event,
      recurringEventUpdateMode,
    }: UpdateEventParams) => {
      const isEventAlreadyRecurring = isRecurringEvent(event)
      const updatedValues = getFormUpdatedFields<EventFormFields>(
        methods.formState.defaultValues,
        formData,
      )

      /**
       * A recurring event is changed to single event.
       */
      const newFrequency = updatedValues.recurrenceConfig?.frequency
      if (
        isEventAlreadyRecurring &&
        newFrequency === RecurringEventFrequency.once
      ) {
        const confirmed = await confirm({
          danger: true,
          title: $t({
            id: 'EventForm.RecurringEvent.Update.ToSingleEvent.Confirmation.Title',
            defaultMessage: 'Update to single event',
          }),
          description: $t({
            id: 'EventForm.RecurringEvent.Update.ToSingleEvent.Confirmation.Description',
            defaultMessage:
              'Are you sure you want to update this event series to a single event?',
          }),
          proceedLabel: $t({
            id: 'Generics.Update',
            defaultMessage: 'Update',
          }),
          cancelLabel: $t({
            id: 'Generics.Cancel',
            defaultMessage: 'Cancel',
          }),
        })
        if (!confirmed) {
          return null
        }

        const updatedEvent = await updateEvent({
          eventId: event.id,
          input: {
            ...createUpdateEventInput(updatedValues),
            recurrenceConfig: {
              frequency: RecurringEventFrequency.once,
            },
            recurringEventUpdateMode: RecurringEventUpdateMode.allEvents,
          },
        })
        return onAfterSubmit(updatedEvent, 'update')
      }

      /**
       * The recurrence config of a single event can be changed without recreating the series.
       */
      if (
        isEventAlreadyRecurring &&
        recurringEventUpdateMode !== RecurringEventUpdateMode.thisEventOnly
      ) {
        const isConfirmed = await getUpdateRecurringEventConfirmation(methods)
        if (!isConfirmed) {
          return null
        }
      }
      const isEventChangedToRecurring =
        !isEventAlreadyRecurring &&
        updatedValues.recurrenceConfig?.frequency &&
        updatedValues.recurrenceConfig?.frequency !==
          RecurringEventFrequency.once

      if (isEventChangedToRecurring) {
        const updatedEvent = await updateEvent({
          eventId: event.id,
          input: {
            ...createUpdateEventInput(updatedValues),
            /**
             * This is a b/e requirement to pass this mode when a single event is changed to recurring.
             */
            recurringEventUpdateMode: RecurringEventUpdateMode.allEvents,
          },
        })
        return onAfterSubmit(updatedEvent, 'update')
      }

      const updatedEvent = await updateEvent({
        eventId: event.id,
        input: {
          ...createUpdateEventInput(updatedValues),
          recurringEventUpdateMode,
        },
      })
      return onAfterSubmit(updatedEvent, 'update')
    },
    [updateEvent, onAfterSubmit, $t, getUpdateRecurringEventConfirmation],
  )

  const submitEvent = useCallback(
    async (
      formData: EventFormFields,
      methods: FormMethods<EventFormFields>,
      recurringEventUpdateMode?: RecurringEventUpdateMode,
    ) => {
      setEnablePrompt(false)

      if (isCreating || isUpdating) {
        return null
      }

      try {
        if (!event?.id) {
          return await create(formData)
        }

        return await update({
          formData,
          methods,
          event,
          recurringEventUpdateMode,
        })
      } catch (error) {
        if (isClientError(error)) {
          const description = error.response.errors.at(0).message
          toast({
            title: $t({
              id: 'Generics.Error',
              defaultMessage: 'Error',
            }),
            description,
            status: 'error',
          })
          const errors = error.response.errors.filter(error => error?.field)
          if (errors.length > 0) {
            errors.forEach(error => {
              methods.setError(error.field, {
                message: error.message,
                type: 'validate',
              })
            })
          }
          return null
        }
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t({
            id: 'Generics.SomethingWentWrong',
            defaultMessage: 'Something went wrong',
          }),
          status: 'error',
        })
      }
      return null
    },
    [setEnablePrompt, event, update, create, isCreating, isUpdating, $t],
  )

  return { submitEvent, isCreating, isUpdating }
}
